import React, { useState, useEffect, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  Button,
  Container,
  Form,
  Row,
  Col,
  Table,
  Card,
  Dropdown,
  DropdownButton,
  ButtonGroup,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  deleteCustomField,
  getCustomFields,
} from "../../redux/slices/customFields";
import Loader from "../../components/spinners/Loader";
import NotyfContext from "../../contexts/NotyfContext";
import useWindowSize from "../../hooks/useWindowSize";
import { useWhitelabel } from "../../contexts/WhitelabelContext";
import {
  FaPencilAlt,
  FaPlus,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaTrashAlt,
} from "react-icons/fa";
import {
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
  defaultColumn,
} from "react-table";
import getCustomFieldsData from "./customFieldsData";
import NewCustomField from "./NewCustomField";
import EditCustomField from "./EditCustomField";
import useDeviceType from "../../hooks/useDeviceType";

const CustomFields = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const customFields = useSelector((state) => state.customFields.customFields);
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const [isLoading, setIsLoading] = useState(true);
  const notyf = useContext(NotyfContext);
  const [activeUser, setActiveUser] = useState({});
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const { backgroundColor, fontColor, faviconUrl } = useWhitelabel();
  const [showNewCustomFieldModal, setShowNewCustomFieldModal] = useState(false);
  const [showEditCustomFieldModal, setShowEditCustomFieldModal] =
    useState(false);
  const [customFieldToEdit, setCustomFieldToEdit] = useState({});
  const { customFieldColumns } = useMemo(() => getCustomFieldsData(t), [t]);

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [user, impersonationUser]);

  useEffect(() => {
    const fetchCustomFields = async () => {
      await dispatch(getCustomFields())
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: {
              x: "center",
              y: "top",
            },
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    if (activeUser.id && activeUser.role !== "admin") {
      fetchCustomFields();
    }
  }, [activeUser, dispatch]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    prepareRow,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: customFieldColumns,
      data: customFields,
      initialState: { pageIndex: 0 },
      defaultColumn,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        ...columns,
        {
          id: t("customFields.actions"),
          Cell: ({ row }) => (
            <DropdownButton
              as={ButtonGroup}
              title={t("customFields.actions")}
              variant="primary"
              data-boundary="viewport"
              style={{
                "--dynamic-bg-color": backgroundColor,
                "--dynamic-font-color": fontColor,
                border: "none",
              }}
            >
              <Dropdown.Item
                eventKey="edit"
                onClick={() => {
                  setCustomFieldToEdit(row.original);
                  setShowEditCustomFieldModal(true);
                }}
              >
                <FaPencilAlt />
                <span className="ms-2">{t("customFields.edit")}</span>
              </Dropdown.Item>
              <Dropdown.Item
                eventKey="delete"
                onClick={async () => {
                  await dispatch(deleteCustomField({ id: row.original.id }))
                    .then(() => {
                      notyf.open({
                        type: "success",
                        message: "Custom Field deleted successfully",
                        duration: 5000,
                        ripple: true,
                        dismissible: false,
                        position: {
                          x: "center",
                          y: "top",
                        },
                      });
                    })
                    .catch((error) => {
                      notyf.open({
                        type: "danger",
                        message: error,
                        duration: 5000,
                        ripple: true,
                        dismissible: false,
                        position: {
                          x: "center",
                          y: "top",
                        },
                      });
                    });
                }}
              >
                <FaTrashAlt />
                <span className="ms-2">{t("customFields.delete")}</span>
              </Dropdown.Item>
            </DropdownButton>
          ),
        },
      ]);
    }
  );

  if (isLoading) {
    return <Loader />;
  } else {
    if (customFields.length === 0) {
      return (
        <React.Fragment>
          <Helmet title="Custom Fields">
            <link rel="icon" href={faviconUrl} />
          </Helmet>
          <Container fluid className="p-0">
            <h1 className="h3 mb-3">{t("customFields.title")}</h1>
            {isDesktop && (
              <div className="mb-3 d-flex justify-content-end align-items-center">
                <Button
                  variant="primary"
                  style={{
                    border: "none",
                    "--dynamic-bg-color": backgroundColor,
                    "--dynamic-font-color": fontColor,
                  }}
                  onClick={() => setShowNewCustomFieldModal(true)}
                >
                  <FaPlus />
                  <span className="ms-2">{t("customFields.new")}</span>
                </Button>
              </div>
            )}
            {!isDesktop && (
              <div className="d-flex justify-content-center flex-column mb-3">
                <Button
                  variant="primary"
                  style={{
                    border: "none",
                    "--dynamic-bg-color": backgroundColor,
                    "--dynamic-font-color": fontColor,
                    width: "100%",
                    padding: "10px",
                  }}
                  onClick={() => setShowNewCustomFieldModal(true)}
                >
                  <FaPlus />
                  <span className="ms-2">{t("customFields.new")}</span>
                </Button>
              </div>
            )}
            <Row>
              <Col>
                <Table>
                  <thead>
                    <tr>
                      <th>{t("No data found.")}</th>
                    </tr>
                  </thead>
                </Table>
              </Col>
            </Row>
            <NewCustomField
              showNewCustomFieldModal={showNewCustomFieldModal}
              setShowNewCustomFieldModal={setShowNewCustomFieldModal}
            />
            <EditCustomField
              showEditCustomFieldModal={showEditCustomFieldModal}
              setShowEditCustomFieldModal={setShowEditCustomFieldModal}
              customFieldToEdit={customFieldToEdit}
            />
          </Container>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <Helmet title={t("customFields.title")} />
        <Container fluid className="p-0">
          <h1 className="h3 mb-3">{t("customFields.title")}</h1>
          <Card>
            <Card.Body>
              {isDesktop && (
                <div className="mb-3 d-flex justify-content-end align-items-center">
                  <Button
                    variant="primary"
                    style={{
                      border: "none",
                      "--dynamic-bg-color": backgroundColor,
                      "--dynamic-font-color": fontColor,
                    }}
                    onClick={() => setShowNewCustomFieldModal(true)}
                  >
                    <FaPlus />
                    <span className="ms-2">{t("customFields.new")}</span>
                  </Button>
                </div>
              )}
              {!isDesktop && (
                <div className="d-flex justify-content-center flex-column mb-3">
                  <Button
                    variant="primary"
                    style={{
                      border: "none",
                      "--dynamic-bg-color": backgroundColor,
                      "--dynamic-font-color": fontColor,
                      width: "100%",
                      padding: "10px",
                    }}
                    onClick={() => setShowNewCustomFieldModal(true)}
                  >
                    <FaPlus />
                    <span className="ms-2">{t("customFields.new")}</span>
                  </Button>
                </div>
              )}
              <Table id="invoicesTable" hover responsive {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()}>
                          <span {...column.getSortByToggleProps()}>
                            {column.render("Header")}
                            <span>
                              {column.sortable ? (
                                column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <FaSortUp className="ms-2 " />
                                  ) : (
                                    <FaSortDown className="ms-2 " />
                                  )
                                ) : (
                                  <FaSort className="ms-2" />
                                )
                              ) : null}
                            </span>
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row, i) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => {
                          return (
                            <td {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <NewCustomField
                showNewCustomFieldModal={showNewCustomFieldModal}
                setShowNewCustomFieldModal={setShowNewCustomFieldModal}
              />
              <EditCustomField
                showEditCustomFieldModal={showEditCustomFieldModal}
                setShowEditCustomFieldModal={setShowEditCustomFieldModal}
                customFieldToEdit={customFieldToEdit}
              />
            </Card.Body>
          </Card>
        </Container>
      </React.Fragment>
    );
  }
};

export default CustomFields;
